import { useEffect, useState } from "react";
import { ApiError, type CalendarChoiceList } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";

/** KEY-818: what may be ticked next — every calendar until the limit is reached, then only those already ticked. */
export function canTick(chosen: ReadonlySet<string>, id: string, max: number): boolean {
  return chosen.has(id) || chosen.size < max;
}

/** KEY-818: the line under an account in Connected calendars — the calendars it shows. */
export function calendarsLine(names: readonly string[] | null | undefined, canChoose: boolean | undefined): string | null {
  if (names && names.length > 0) return names.join(", ");
  return canChoose ? "Main calendar" : null;
}

/**
 * KEY-818: which of an account's calendars to show — family, work, a shared one — beside its main calendar. The list
 * is read live from Google or Outlook each time it opens, so a calendar made there since is offered straight away.
 * Saving syncs, so the person's dials show the new choice when it closes.
 */
export function CalendarChooser({ connectionId, onSaved, onClose }: { connectionId: string; onSaved: () => void; onClose: () => void }) {
  const [list, setList] = useState<CalendarChoiceList | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    let live = true;
    api
      .calendarChoices(token, connectionId)
      .then((l) => {
        if (!live) return;
        setList(l);
        setChosen(new Set(l.calendars.filter((c) => c.chosen).map((c) => c.id)));
      })
      .catch((e) => live && setError(e instanceof ApiError && e.message ? e.message : "The calendars in this account couldn't be read just now."));
    return () => {
      live = false;
    };
  }, [connectionId]);

  const toggle = (id: string) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    const token = getAccessToken();
    if (!token || chosen.size === 0 || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api.chooseCalendars(token, connectionId, [...chosen]);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "That choice couldn't be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="group"
      aria-label="Calendars to show"
      style={{ flexBasis: "100%", display: "flex", flexDirection: "column", gap: 8, padding: "10px 12px", borderRadius: "var(--radius-sm)", border: "var(--border-w) solid var(--border)" }}
    >
      {!list && !error && <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Reading the calendars in this account…</div>}
      {list && (
        <>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Tick the calendars your dials should show, up to {list.maxChosen}.
          </div>
          {list.calendars.map((c) => (
            <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: "var(--text)" }}>
              <input
                type="checkbox"
                checked={chosen.has(c.id)}
                disabled={!canTick(chosen, c.id, list.maxChosen)}
                onChange={() => toggle(c.id)}
              />
              <span
                aria-hidden
                style={{ width: 10, height: 10, borderRadius: 3, flex: "none", background: c.colour ?? "var(--border)" }}
              />
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</span>
              {c.isPrimary && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>main</span>}
            </label>
          ))}
          {chosen.size === 0 && <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Tick at least one. To stop reading this account, remove it.</div>}
        </>
      )}
      {error && <div role="alert" style={{ fontSize: 13, color: "var(--danger, #b42318)" }}>{error}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        {list && (
          <button type="button" className="kd-btn kd-btn-sm" disabled={saving || chosen.size === 0} onClick={() => void save()}>
            {saving ? "Saving…" : "Save"}
          </button>
        )}
        <button type="button" className="kd-btn kd-btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
